<div class="crm-panel">
  <div class="crm-actions-row">
    <button type="button" class="crm-btn ghost" (click)="openCreateTask()" [disabled]="isCreateTaskOpen">
      + Create Task
    </button>

    <button type="button" class="sort-chip" (click)="toggleTaskSort()">
      Sort: {{ (taskSortOrder$ | async) === 'desc' ? 'New - Old' : 'Old - New' }}
      <mat-icon>swap_vert</mat-icon>
    </button>
  </div>

  <div class="crm-filter-row">
    <mat-form-field appearance="outline" class="crm-field compact">
      <mat-label>Time Range</mat-label>
      <mat-select [value]="(taskTimeRange$ | async) || 'all'" (selectionChange)="onTaskTimeRangeChange($event.value)">
        <mat-option *ngFor="let option of taskTimeRangeOptions" [value]="option.value">{{ option.label }}</mat-option>
      </mat-select>
    </mat-form-field>
  </div>

  <div class="crm-banner error" *ngIf="tasksErrorMessage">{{ tasksErrorMessage }}</div>
  <div class="crm-banner error" *ngIf="taskAttachmentErrorMessage">{{ taskAttachmentErrorMessage }}</div>
  <div class="crm-banner info" *ngIf="createTaskSuccessMessage && !isCreateTaskOpen">{{ createTaskSuccessMessage }}</div>

  <form class="crm-form" [formGroup]="createTaskForm" (ngSubmit)="submitTask()" *ngIf="isCreateTaskOpen">
    <mat-form-field appearance="outline" class="crm-field">
      <mat-label>Subject</mat-label>
      <input matInput formControlName="taskSubject" maxlength="200" />
      <mat-error *ngIf="createTaskForm.get('taskSubject')?.invalid && createTaskForm.get('taskSubject')?.touched">Subject is required</mat-error>
    </mat-form-field>

    <div class="crm-field crm-editor-field">
      <div class="crm-editor-label">Description*</div>
      <app-ckeditor
        [data]="createTaskForm.get('taskDescription')?.value || ''"
        [height]="'150px'"
        (contentChanged)="onTaskDescriptionChanged($event)">
      </app-ckeditor>
      <div class="crm-editor-error" *ngIf="createTaskForm.get('taskDescription')?.invalid && createTaskForm.get('taskDescription')?.touched">
        Description is required
      </div>
    </div>

    <div class="crm-grid-2">
      <mat-form-field appearance="outline" class="crm-field">
        <mat-label>Due Date</mat-label>
        <input matInput [matDatepicker]="dueDatePicker" formControlName="taskDueDate" />
        <mat-datepicker-toggle matSuffix [for]="dueDatePicker"></mat-datepicker-toggle>
        <mat-datepicker #dueDatePicker></mat-datepicker>
      </mat-form-field>

      <mat-form-field appearance="outline" class="crm-field">
        <mat-label>Due Time</mat-label>
        <input matInput type="time" formControlName="taskDueTime" />
      </mat-form-field>
    </div>

    <div class="crm-grid-3">
      <mat-form-field appearance="outline" class="crm-field">
        <mat-label>Priority</mat-label>
        <mat-select formControlName="taskPriority">
          <mat-option *ngFor="let p of taskPriorityOptions" [value]="p.value">{{ p.label }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="crm-field">
        <mat-label>Status</mat-label>
        <mat-select formControlName="taskStatus">
          <mat-option *ngFor="let s of taskStatusOptions" [value]="s.value">{{ s.label }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="outline" class="crm-field">
        <mat-label>Duration (minutes)</mat-label>
        <input matInput type="number" formControlName="taskDuration" min="1" />
      </mat-form-field>
    </div>

    <mat-checkbox formControlName="taskSendEmail">Send email notification</mat-checkbox>

    <div class="crm-attachment-row">
      <div class="crm-editor-label">Attachment</div>
      <input
        #taskAttachmentInput
        type="file"
        class="task-file-input-hidden"
        (change)="onTaskAttachmentSelected($event)"
        [disabled]="isSubmittingTask" />
      <div class="task-file-picker">
        <button
          type="button"
          class="task-file-trigger"
          (click)="taskAttachmentInput.click()"
          [disabled]="isSubmittingTask">
          Choose File
        </button>
        <ng-container *ngIf="selectedTaskAttachmentName; else noTaskAttachmentSelected">
          <div class="task-attachment-meta">
            <span class="task-attachment-name">{{ selectedTaskAttachmentName }}</span>
            <span class="task-attachment-size">({{ formatAttachmentSize(selectedTaskAttachmentSize) }})</span>
            <button type="button" class="note-expand" (click)="removeTaskAttachment(taskAttachmentInput)">Remove</button>
          </div>
        </ng-container>
        <ng-template #noTaskAttachmentSelected>
          <span class="task-attachment-empty">No file chosen</span>
        </ng-template>
      </div>
      <div class="task-attachment-help">Max file size: 5MB</div>
    </div>

    <div class="crm-banner error" *ngIf="createTaskErrorMessage">{{ createTaskErrorMessage }}</div>

    <div class="crm-form-actions">
      <button type="submit" class="crm-btn" [disabled]="createTaskForm.invalid || isSubmittingTask">
        {{ isSubmittingTask ? 'Saving...' : 'Create Task' }}
      </button>
      <button type="button" class="crm-btn ghost" (click)="toggleCreateTask()">Cancel</button>
    </div>
  </form>

  <div class="crm-banner info" *ngIf="(tasks$ | async)?.length === 0 && hasRequestedTasks && !(tasksFetching$ | async)">
    No tasks found for this client selection.
  </div>

  <div class="crm-list" *ngIf="tasks$ | async as tasks">
    <div class="crm-list-item" *ngFor="let task of tasks">
      <div class="task-card-header">
        <div class="task-author-section">
          <div class="task-avatar">{{ getTaskInitials(task.ownerName) }}</div>
          <div class="task-author-info">
            <div class="task-author-name">{{ task.ownerName || 'Unknown User' }}</div>
            <div class="item-meta">{{ formatTaskDate(task.createdon) }}</div>
          </div>
        </div>
        <div class="task-regarding" *ngIf="getTaskRegardingName(task)">
          Regarding: {{ getTaskRegardingName(task) }}
        </div>
      </div>

      <div class="item-title">{{ task.subject || 'Untitled task' }}</div>
      <ng-container *ngIf="task.description; else noTaskDescription">
        <div #taskBodyEl class="item-body" [class.clamped]="!task.isExpanded" [innerHTML]="sanitizeTaskDescription(task.description)"></div>
        <button class="see-more-btn" type="button" *ngIf="task.showSeeMore || task.isExpanded" (click)="toggleTaskExpand(task)">
          {{ task.isExpanded ? 'See less' : 'See more' }}
        </button>
      </ng-container>
      <div class="task-attachment" *ngIf="task.filename">
        <mat-icon>attach_file</mat-icon>
        <button
          type="button"
          class="task-attachment-link"
          [disabled]="isDownloadingAttachment$ | async"
          [title]="(isDownloadingAttachment$ | async) ? 'Downloading attachment...' : 'Download attachment - ' + task.filename"
          (click)="downloadTaskAttachment(task)">
          {{ task.filename }}
        </button>
      </div>
      <div class="task-footer-row">
        <div class="item-meta-row">
          <span class="pill priority-pill">{{ getTaskPriorityLabel(task) }}</span>
          <span class="pill status-pill" [class.status-pill-completed]="isTaskCompleted(task)">{{ getTaskStatusLabel(task) }}</span>
          <span class="item-meta" *ngIf="task.scheduledend">Due: {{ task.scheduledend | date:'MM/dd/yyyy' }}</span>
        </div>
        <button
          *ngIf="!isTaskCompleted(task)"
          type="button"
          class="crm-btn close-btn"
          [disabled]="closingTaskId === task.activityid"
          (click)="closeTask(task)">
          {{ closingTaskId === task.activityid ? 'Closing...' : 'Close Task' }}
        </button>
      </div>
    </div>
  </div>
</div>

<ng-template #noTaskDescription>
  <div class="item-body">No description</div>
</ng-template>
